import _extends from "@babel/runtime/helpers/extends";
import { StylesProvider, ThemeProvider, createGenerateClassName, jssPreset, withStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import { create } from "jss";
import increaseSpecificity from "jss-increase-specificity";
import { SnackbarProvider } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { isWindowAvailable } from "../Services/windowService";
import { styles as snackBarStyles } from "../SnackBarContent/styles";
import { createTheme } from "../Utilities/ThemeHelper";
const StyledSnackbarProvider = withStyles(snackBarStyles)(SnackbarProvider);
const muiGenerateClassName = createGenerateClassName({
  productionPrefix: ""
});
const addSpecificity = ":not(#\\u0010)";
const cssResetProps = `{
	height: auto;
	background: none;
	background-image: none;
	box-shadow: none;
	margin: 0;
	padding: 0;
	border: 0;
	font-family: inherit;
	font-size: inherit;
	color:inherit;
}`;
export const ProductThemeProvider = props => {
  const {
    children,
    cssReset = false,
    maxSnackbars = 3,
    SnackbarProps,
    SnackbarProviderProps = {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
      },
      hideIconVariant: true
    },
    prefix = "cpp",
    specificity = 0,
    product,
    primaryHex = "",
    title,
    themeOverride,
    maxBreakpoint
  } = props;
  const renderedTitle = title || undefined;
  useEffect(() => {
    if (maxBreakpoint && !["xs", "sm", "md", "lg", "xl"].includes(maxBreakpoint)) {
      // eslint-disable-next-line no-console
      console.warn("Unrecognized maxBreakpoint:", maxBreakpoint);
    }
  }, [maxBreakpoint]);
  useEffect(() => {
    if (product && !["engageCentral", "engageEvolve", "hr", "clerk", "rec", "ready", "preamble"].includes(product)) {
      // eslint-disable-next-line no-console
      console.warn("Unrecognized product:", product);
    }
  }, [product]);
  const makeTheme = useCallback(() => createTheme(product, primaryHex, renderedTitle, cssReset, themeOverride, maxBreakpoint), [cssReset, maxBreakpoint, primaryHex, product, themeOverride, renderedTitle]);
  const [theme, setTheme] = useState(makeTheme());
  const jss = useRef(create({
    plugins: [...jssPreset().plugins]
  }));
  useEffect(() => {
    if (specificity > 0) {
      jss.current.use(increaseSpecificity({
        repeat: specificity
      }));
    }
  }, [jss, specificity]);
  useEffect(() => {
    setTheme(makeTheme());
  }, [makeTheme]);
  const makeSelector = (selector, selectorSpecificity) => {
    const specificityToAdd = specificity - selectorSpecificity < 0 ? 0 : specificity - selectorSpecificity;
    return specificity ? `${selector}${addSpecificity.repeat(specificityToAdd)}` : selector;
  };
  if (cssReset && isWindowAvailable()) {
    let resetElement = document.getElementById("preamble-reset");

    // This selector will catch all jss-styled items
    const preambleSelector = makeSelector(`[class^="${prefix}-"]`, 1);

    // jQuery UI adds a bunch of classnames, this catches those
    const jqueryUiSelector = `${makeSelector(`[class^="${prefix}-"]`, 2)} [class^="ui-"]`;

    // Sometimes we have styles on nested JSS selectors
    const preambleNestedSelector = `${makeSelector(`[class^="${prefix}-"]`, 2)} > *`;
    if (!resetElement) {
      const head = document.getElementsByTagName("head")[0];
      resetElement = document.createElement("style");
      resetElement.id = "preamble-reset";
      head.insertBefore(resetElement, head.firstChild);
    }
    resetElement.innerHTML = `${preambleSelector},${preambleNestedSelector},${jqueryUiSelector}${cssResetProps}`;
  }
  const generateClassNameDefault = (rule, sheet) => `${prefix}-${muiGenerateClassName(rule, sheet)}`;
  return /*#__PURE__*/React.createElement(StylesProvider, {
    jss: jss.current,
    generateClassName: generateClassNameDefault
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(StyledSnackbarProvider, _extends({
    maxSnack: maxSnackbars,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center"
    },
    hideIconVariant: true
  }, SnackbarProps, SnackbarProviderProps, {
    classes: {
      ...SnackbarProps?.classes,
      ...SnackbarProviderProps?.classes,
      containerRoot: classNames("prmbl-snackbarProvider", SnackbarProps?.classes?.containerRoot, SnackbarProviderProps?.classes?.containerRoot),
      root: classNames("prmbl-snackbarProvider-root", SnackbarProps?.classes?.root, SnackbarProviderProps?.classes?.root)
    }
  }), children)));
};
ProductThemeProvider.defaultProps = {
  primaryHex: "",
  specificity: 0,
  cssReset: false,
  maxSnackbars: 3,
  prefix: "cpp",
  SnackbarProps: {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center"
    },
    hideIconVariant: true
  }
};
export default ProductThemeProvider;